import React, { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, IconButton, Alert, Divider, Stack, Paper, Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../reduxStore/Store';
import {
  fetchPlafondsByTiers, createOrUpdatePlafond, deletePlafond, clearPlafonds, clearMessages,
} from '../../reduxStore/TiersPlafondSlice';
import { TiersPlafond, Departement } from '../../models/models';
import { OptionKey } from '../../constants/enums';
import { useOption } from '../../hooks/useOption';
import SelectDepartement from '../Selects/SelectDepartement';
import { useSnackbar } from '../SnackbarProvider';

interface Props {
  tiersId: number;
}

/**
 * Section "Plafonds de crédit" — gère un plafond global (departement_id NULL) + N plafonds
 * par département pour un tier donné.
 *
 * UX :
 *  - Ligne "Plafond global" toujours visible (créée à la volée si absente)
 *  - Liste des plafonds dept existants (dept en lecture seule une fois saved)
 *  - Bouton "Ajouter un plafond départemental" pour créer une nouvelle ligne en édition
 *
 * Côté backend, la SP gère l'upsert par (tiers_id, departement_id) — on peut donc
 * envoyer id=-1 et la SP update si la clé existe.
 */
const TiersPlafondSection: React.FC<Props> = ({ tiersId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();
  const { plafonds, loading, error, successMessage } = useSelector(
    (state: RootState) => state.TiersPlafondSlice
  );

  // Lecture des options de vérification — défaut true (cohérent avec backend).
  // Si OFF → les plafonds saisis sont inertes : on alerte l'utilisateur.
  const checkDept   = useOption<boolean>(OptionKey.TIERS_CHECK_PLAFOND_DEPT,   true);
  const checkGlobal = useOption<boolean>(OptionKey.TIERS_CHECK_PLAFOND_GLOBAL, true);

  // État local pour les valeurs en cours d'édition (clé = id ou 'global' ou 'new:N')
  const [editing, setEditing]   = useState<Record<string, number | string>>({});
  const [newRows, setNewRows]   = useState<{ key: string; dept: Departement | null; plafond: string }[]>([]);
  const [nextNewId, setNextNewId] = useState(0);

  useEffect(() => {
    dispatch(fetchPlafondsByTiers({ tiers_id: tiersId }));
    return () => { dispatch(clearPlafonds()); };
  }, [dispatch, tiersId]);

  useEffect(() => {
    if (successMessage) {
      showSnackbar(successMessage, 'success');
      dispatch(clearMessages());
    }
    if (error) {
      showSnackbar(error, 'error');
      dispatch(clearMessages());
    }
  }, [successMessage, error, dispatch, showSnackbar]);

  // Sépare le plafond global des plafonds par dept
  const globalPlafond = plafonds.find(p => p.departement_id == null);
  const deptPlafonds  = plafonds.filter(p => p.departement_id != null);

  const handleSaveGlobal = async () => {
    const valStr = editing['global'] ?? globalPlafond?.plafond ?? '';
    const val = Number(valStr);
    if (Number.isNaN(val) || val < 0) {
      showSnackbar('Plafond invalide', 'error');
      return;
    }
    await dispatch(createOrUpdatePlafond({
      id:             globalPlafond?.id ?? -1,
      tiers_id:       tiersId,
      departement_id: null,
      plafond:        val,
    }));
  };

  const handleSaveDept = async (existing: TiersPlafond) => {
    const valStr = editing[String(existing.id)] ?? existing.plafond;
    const val = Number(valStr);
    if (Number.isNaN(val) || val < 0) {
      showSnackbar('Plafond invalide', 'error');
      return;
    }
    await dispatch(createOrUpdatePlafond({
      id:             existing.id,
      tiers_id:       tiersId,
      departement_id: existing.departement_id,
      plafond:        val,
    }));
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Supprimer ce plafond ?')) return;
    await dispatch(deletePlafond(id));
  };

  const handleAddNewRow = () => {
    const key = `new:${nextNewId}`;
    setNewRows(prev => [...prev, { key, dept: null, plafond: '' }]);
    setNextNewId(n => n + 1);
  };

  const handleSaveNewRow = async (key: string) => {
    const row = newRows.find(r => r.key === key);
    if (!row || !row.dept) {
      showSnackbar('Sélectionnez un département', 'error');
      return;
    }
    const val = Number(row.plafond);
    if (Number.isNaN(val) || val < 0) {
      showSnackbar('Plafond invalide', 'error');
      return;
    }
    // Vérification doublon côté UI (la SP gère aussi l'upsert)
    if (deptPlafonds.some(p => p.departement_id === row.dept!.id)) {
      showSnackbar('Un plafond existe déjà pour ce département (modifiez-le directement)', 'error');
      return;
    }
    await dispatch(createOrUpdatePlafond({
      id:             -1,
      tiers_id:       tiersId,
      departement_id: row.dept.id,
      plafond:        val,
    }));
    setNewRows(prev => prev.filter(r => r.key !== key));
  };

  const handleRemoveNewRow = (key: string) => {
    setNewRows(prev => prev.filter(r => r.key !== key));
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Plafonds de crédit
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        Le plafond global s'applique tous départements confondus. Les plafonds départementaux
        s'ajoutent en intersection : la balance dans un dept ne doit pas dépasser son plafond
        ET la balance globale ne doit pas dépasser le plafond global. Vide = aucune limite.
      </Typography>

      {loading && <Alert severity="info" sx={{ mb: 1 }}>Chargement…</Alert>}

      {/* Alertes : si une option de check est OFF, les plafonds correspondants sont inertes. */}
      {!checkDept && !checkGlobal && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Les contrôles de plafond sont entièrement désactivés (Options → tiers.check_plafond_dept et tiers.check_plafond_global).
          Les valeurs ci-dessous sont enregistrées mais non vérifiées à l'insertion des lignes.
        </Alert>
      )}
      {!checkDept && checkGlobal && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Le contrôle départemental est désactivé (Options → tiers.check_plafond_dept).
          Seul le plafond global sera vérifié.
        </Alert>
      )}
      {checkDept && !checkGlobal && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Le contrôle global est désactivé (Options → tiers.check_plafond_global).
          Seuls les plafonds départementaux seront vérifiés.
        </Alert>
      )}

      {/* Plafond global */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Chip label="Global" size="small" color="primary" sx={{ minWidth: 100 }} />
        <TextField
          type="number"
          label="Plafond"
          size="small"
          value={editing['global'] ?? globalPlafond?.plafond ?? ''}
          onChange={(e) => setEditing(s => ({ ...s, global: e.target.value }))}
          sx={{ flex: 1 }}
          inputProps={{ min: 0, step: 0.01 }}
        />
        <IconButton color="primary" onClick={handleSaveGlobal} title="Enregistrer le plafond global">
          <SaveIcon />
        </IconButton>
        {globalPlafond && (
          <IconButton color="error" onClick={() => handleDelete(globalPlafond.id!)} title="Supprimer le plafond global">
            <DeleteIcon />
          </IconButton>
        )}
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* Plafonds par département */}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Plafonds par département
      </Typography>

      <Stack spacing={1}>
        {deptPlafonds.map(p => (
          <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label={p.departement_nom ?? `Dept #${p.departement_id}`} size="small" sx={{ minWidth: 140 }} />
            <TextField
              type="number"
              label="Plafond"
              size="small"
              value={editing[String(p.id)] ?? p.plafond}
              onChange={(e) => setEditing(s => ({ ...s, [String(p.id)]: e.target.value }))}
              sx={{ flex: 1 }}
              inputProps={{ min: 0, step: 0.01 }}
            />
            <IconButton color="primary" onClick={() => handleSaveDept(p)} title="Enregistrer">
              <SaveIcon />
            </IconButton>
            <IconButton color="error" onClick={() => handleDelete(p.id!)} title="Supprimer">
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}

        {newRows.map(row => (
          <Box key={row.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ minWidth: 200 }}>
              <SelectDepartement
                onDepartementChange={(d) =>
                  setNewRows(prev => prev.map(r => r.key === row.key ? { ...r, dept: d } : r))
                }
                selectedDepartement={row.dept}
              />
            </Box>
            <TextField
              type="number"
              label="Plafond"
              size="small"
              value={row.plafond}
              onChange={(e) =>
                setNewRows(prev => prev.map(r => r.key === row.key ? { ...r, plafond: e.target.value } : r))
              }
              sx={{ flex: 1 }}
              inputProps={{ min: 0, step: 0.01 }}
            />
            <IconButton color="primary" onClick={() => handleSaveNewRow(row.key)} title="Enregistrer">
              <SaveIcon />
            </IconButton>
            <IconButton color="default" onClick={() => handleRemoveNewRow(row.key)} title="Annuler">
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
      </Stack>

      <Box sx={{ mt: 1.5 }}>
        <IconButton color="primary" onClick={handleAddNewRow} title="Ajouter un plafond départemental">
          <AddIcon /> <Typography variant="body2" sx={{ ml: 0.5 }}>Ajouter un plafond départemental</Typography>
        </IconButton>
      </Box>
    </Paper>
  );
};

export default TiersPlafondSection;
